import React from 'react';
import I18n from 'i18n-js';
import { useModalManager } from '@/components/common/Modal';
import ConfirmDeleteModal from '@/components/common/Modal/ConfirmDeleteModal';
import Icon from '@/components/ui/Icon';
import Tooltip from '@/components/ui/Tooltip';
import { IAnalyticRubricCriterion } from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import cn from '@/utils/cn';
import { UnstyledButton } from '@/components/ui/Buttons';

const getTextPreview = (html: string, maxLength = 50): string => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = doc.body.textContent?.trim() ?? '';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

interface DeleteCriterionButtonProps {
  className?: string;
  criterionData: IAnalyticRubricCriterion;
  locale: Lowercase<Locale>;
  onDelete: () => void;
  totalCriteria: number;
  cellFocused: boolean;
}

const DeleteCriterionButton = ({
  className,
  criterionData,
  locale,
  onDelete,
  totalCriteria,
  cellFocused,
}: DeleteCriterionButtonProps) => {
  const { isOpen, open, close } = useModalManager();

  const isDisabled = totalCriteria <= 1;

  // Get criterion name for preview
  const criterionName = locale === 'en' ? criterionData.name_en : criterionData.name_es;
  const textPreview = getTextPreview(criterionName);

  // Tooltip for disabled state
  const tooltipContent = isDisabled
    ? I18n.t('delete_criterion_min_disabled', { locale })
    : '';

  const handleDelete = () => {
    onDelete();
    close();
  };

  const confirmationText = I18n.t('delete_criterion_confirmation', {
    locale,
    preview: textPreview || I18n.t('empty', { locale }),
  });

  return (
    <Tooltip.Provider>
      <Tooltip>
        <Tooltip.Portal>
          <Tooltip.Content
            className="tw-text-sm tw-shadow tw-p-2 tw-bg-white tw-rounded tw-text-dark-grey tw-z-9999"
            disabled={!isDisabled}
          >
            {tooltipContent}
            <Tooltip.Arrow className="tw-fill-white" />
          </Tooltip.Content>
        </Tooltip.Portal>
        <Tooltip.Trigger asChild>
          <span className="tw-absolute tw-top-2 tw-right-2 tw-z-10">
            <UnstyledButton
              aria-label={I18n.t('delete_criterion', { locale })}
              className={cn(
                'tw-border-none tw-bg-transparent tw-flex tw-justify-center tw-items-center',
                'tw-cursor-pointer tw-transition-opacity focus:tw-opacity-100 hover:tw-opacity-100',
                'disabled:tw-cursor-not-allowed disabled:tw-opacity-40',
                { 'tw-opacity-100': cellFocused },
                { 'tw-opacity-0': !cellFocused },
                className,
              )}
              disabled={isDisabled}
              type="button"
              onClick={open}
            >
              <Icon className="tw-text-red" icon="Delete" size={16} />
            </UnstyledButton>
          </span>
        </Tooltip.Trigger>
      </Tooltip>

      <ConfirmDeleteModal
        confirmationText={confirmationText}
        headerText={I18n.t('delete_criterion', { locale })}
        isLoading={false}
        isOpen={isOpen}
        locale={locale}
        onCancel={close}
        onConfirm={handleDelete}
      />
    </Tooltip.Provider>
  );
};

export default DeleteCriterionButton;
